import { Box, Typography, Stack, Switch, FormControlLabel, Alert, CircularProgress } from '@mui/material';
import { AccountBalance as BalanceIcon, Email as EmailIcon, Event as EventIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNotificationSettings, useUpdateNotificationSettings } from '../../hooks/useNotifications';

export default function NotificationsSettings() {
  const { t } = useTranslation(['profile', 'common']);
  const { data: settings, isLoading, error } = useNotificationSettings();
  const updateSettings = useUpdateNotificationSettings();

  const handleLowBalanceToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateSettings.mutate({ low_balance_enabled: event.target.checked });
  };

  const handleActivityFollowUpToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateSettings.mutate({ activity_follow_up_enabled: event.target.checked });
  };

  const handleWeeklyIdeaToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateSettings.mutate({ weekly_event_enabled: event.target.checked });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {t('profile:notifications.loadError', { defaultValue: 'Failed to load notification settings' })}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
        {t('profile:notifications.title', { defaultValue: 'Notifications' })}
      </Typography>

      <Stack spacing={4}>
        {/* Activity Follow-up */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <EmailIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('profile:notifications.activityFollowUp.title', { defaultValue: 'Activity Follow-up' })}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('profile:notifications.activityFollowUp.description', {
              defaultValue: 'Receive an email when you haven’t logged in for a while (e.g. 7+ days) to remind you to come back.'
            })}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={settings?.activity_follow_up_enabled ?? true}
                onChange={handleActivityFollowUpToggle}
                color="primary"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#8b9cff' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#8b9cff' },
                }}
              />
            }
            label={
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {settings?.activity_follow_up_enabled
                  ? t('profile:notifications.activityFollowUp.enabled', { defaultValue: 'Enabled' })
                  : t('profile:notifications.activityFollowUp.disabled', { defaultValue: 'Disabled' })}
              </Typography>
            }
            sx={{ m: 0, '& .MuiFormControlLabel-label': { ml: 1.5 } }}
          />
        </Box>

        {/* Weekly content idea */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <EventIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('profile:notifications.weeklyIdea.title', { defaultValue: 'Weekly Content Ideas' })}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('profile:notifications.weeklyIdea.description', {
              defaultValue: 'Receive a weekly email with a content idea tailored to your business profile.'
            })}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={settings?.weekly_event_enabled ?? true}
                onChange={handleWeeklyIdeaToggle}
                color="primary"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#8b9cff' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#8b9cff' },
                }}
              />
            }
            label={
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {settings?.weekly_event_enabled
                  ? t('profile:notifications.weeklyIdea.enabled', { defaultValue: 'Enabled' })
                  : t('profile:notifications.weeklyIdea.disabled', { defaultValue: 'Disabled' })}
              </Typography>
            }
            sx={{ m: 0, '& .MuiFormControlLabel-label': { ml: 1.5 } }}
          />
        </Box>

        {/* Low Balance Notification */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <BalanceIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('profile:notifications.lowBalance.title', { defaultValue: 'Low Balance Alerts' })}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('profile:notifications.lowBalance.description', {
              defaultValue: 'Receive email notifications when your content generation balance is running low (20% or less remaining).'
            })}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={settings?.low_balance_enabled ?? true}
                onChange={handleLowBalanceToggle}
                color="primary"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#8b9cff' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#8b9cff' },
                }}
              />
            }
            label={
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {settings?.low_balance_enabled
                  ? t('profile:notifications.lowBalance.enabled', { defaultValue: 'Enabled' })
                  : t('profile:notifications.lowBalance.disabled', { defaultValue: 'Disabled' })}
              </Typography>
            }
            sx={{ m: 0, '& .MuiFormControlLabel-label': { ml: 1.5 } }}
          />
        </Box>

        {updateSettings.isError && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }} onClose={() => updateSettings.reset()}>
            {t('profile:notifications.updateError', { defaultValue: 'Failed to update notification settings. Please try again.' })}
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
